import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/** Every possible step, in order. Changing a choice clears the choices after it. */
const ORDER = ['model', 'color', 'size', 'deco', 'pcolor', 'cut', 'shape', 'place', 'upload', 'qty'];
/** Steps saved on the order as line item properties, named by their panel's `data-property`. */
const PROPERTY_STEPS = ['pcolor', 'cut', 'shape', 'place'];
const VECTOR_TYPES = ['svg', 'ai', 'eps', 'pdf'];
const RASTER_TYPES = ['png', 'jpg', 'jpeg'];

/**
 * @typedef {object} Choice
 * @property {string} value - The name shown to the customer and saved on the order.
 * @property {string} [id] - The Shopify option value id, for color, size and decoration.
 * @property {string} [url] - The product URL, for the model.
 * @property {boolean} [auto] - True when the size was set because the color has only one.
 * @property {boolean} [shapes] - True when the outline choice asks for a default shape.
 */

/**
 * One line of the quantity step: a color and size of the chosen hat, with the variant
 * Shopify resolved for it and the chosen decoration.
 * @typedef {object} Row
 * @property {string} colorId
 * @property {string} colorName
 * @property {string} sizeId
 * @property {string} sizeName
 * @property {{ id: string, name: string }[]} sizes - The sizes that exist for the color.
 * @property {string} variantId
 * @property {number} cents - The variant's price in minor units.
 * @property {number} quantity
 */

/**
 * @typedef {object} HatBuilderRefs
 * @property {HTMLFormElement} form
 * @property {HTMLElement} fallback
 * @property {HTMLElement} chips
 * @property {HTMLElement} empty
 * @property {HTMLElement} price
 * @property {HTMLElement} priceNote
 * @property {HTMLElement} stage
 * @property {HTMLElement} progress
 * @property {HTMLElement} status
 * @property {HTMLElement} sizeNote
 * @property {HTMLElement} [dtfNote]
 * @property {HTMLInputElement} file
 * @property {HTMLElement} artworkField
 * @property {HTMLElement} [uploadHelp]
 * @property {HTMLElement} embroideryFiles
 * @property {HTMLInputElement} stitchFile
 * @property {HTMLInputElement} pdfFile
 * @property {HTMLElement} stitchMessage
 * @property {HTMLElement} pdfMessage
 * @property {HTMLElement} [digitizeBox]
 * @property {HTMLButtonElement} [digitize]
 * @property {HTMLElement} vectorMessage
 * @property {HTMLElement} rasterMessage
 * @property {HTMLElement} typeMessage
 * @property {HTMLButtonElement} [vectorize]
 * @property {HTMLElement} [vectorizeNotice]
 * @property {HTMLButtonElement} submit
 * @property {HTMLFieldSetElement} [stock]
 * @property {HTMLElement} rows
 * @property {HTMLButtonElement} addRow
 * @property {HTMLElement} total
 * @property {HTMLTemplateElement} rowTemplate
 * @property {HTMLTemplateElement} [moneyFormat]
 * @property {HTMLButtonElement} back
 * @property {HTMLButtonElement} next
 * @property {HTMLElement} fields
 */

/**
 * Step-by-step hat builder. Colors, sizes, decorations, prices and variant ids are read
 * from sections/hat-builder-product.liquid through the Section Rendering API, so a
 * combination with no variant is never offered and never sent to the cart.
 *
 * @extends {Component<HatBuilderRefs>}
 */
class HatBuilder extends Component {
  requiredRefs = ['form', 'chips', 'price', 'stage', 'progress', 'status', 'sizeNote', 'file', 'stitchFile', 'pdfFile', 'submit', 'rows', 'addRow', 'total', 'rowTemplate', 'back', 'next', 'fields'];

  /** @type {Record<string, Choice | undefined>} */
  #state = {};
  #current = 0;
  /** Option value ids sent to Shopify to resolve variants. */
  #ids = { color: '', size: '', deco: '' };
  #hasSizeOption = false;
  /** How many sizes exist for the chosen color. */
  #sizeChoices = 0;
  /** @type {{ id: string, price: string, cents: number } | null} */
  #variant = null;
  /** @type {Row[]} */
  #rows = [];
  #fileType = '';
  #vectorize = false;
  /** Embroidery only: the same service, offered as "digitizing" in place of the two files. */
  #digitize = false;
  #busy = false;
  #sending = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #sendingTimer;
  /** Theme editor only: the panel of the block being edited. */
  #previewPanel = '';
  #previewDecoration = '';
  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {AbortController | undefined} */
  #request;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    const { form, fallback } = this.refs;

    this.addEventListener('click', this.#onClick, { signal });
    form.addEventListener('submit', this.#onSubmit, { signal });
    form.addEventListener('change', this.#onFileChange, { signal });
    form.addEventListener('input', this.#onRowInput, { signal });
    this.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    this.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });
    // Coming back with the browser's back button: allow sending again.
    window.addEventListener('pageshow', () => this.#setSending(false), { signal });

    if (fallback) fallback.hidden = true;
    form.hidden = false;

    const wanted = new URLSearchParams(window.location.search).get('model');
    const tile = wanted ? this.#tiles('model').find((candidate) => candidate.dataset.handle === wanted) : undefined;
    if (tile) this.#choose(tile);
    else this.#render(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#request?.abort();
    clearTimeout(this.#sendingTimer);
  }

  /**
   * @param {string} key
   * @returns {HTMLElement | null}
   */
  #panel(key) {
    return this.querySelector(`[data-panel="${key}"]`);
  }

  /**
   * @param {string} key
   * @returns {HTMLElement[]} Every tile of a step, shown or not.
   */
  #tiles(key) {
    return Array.from(this.querySelectorAll(`.hat-builder__tile[data-step="${key}"]`));
  }

  /** @returns {'patch' | 'embroidery' | 'print'} What the chosen decoration's name asks for. */
  #kind() {
    const name = (this.#state.deco?.value ?? '').toLowerCase();
    if (name.includes('patch')) return 'patch';
    if (name.includes('embroidery')) return 'embroidery';
    return 'print';
  }

  /**
   * The tiles a customer may pick on a step. A size or decoration with no purchasable
   * variant for the current choices is left out.
   * @param {string} key
   * @returns {HTMLElement[]}
   */
  #choices(key) {
    const tiles = this.#tiles(key);
    if (key === 'size' || key === 'deco') {
      return tiles.filter((tile) => tile.dataset.variantId && tile.dataset.variantAvailable === 'true');
    }
    if (key === 'pcolor') {
      const decoration = this.#previewDecoration || this.#state.deco?.value;
      return tiles.filter((tile) => tile.dataset.decoration === decoration);
    }
    if (key === 'place') return tiles.filter((tile) => tile.dataset.for === this.#kind());
    return tiles;
  }

  /** @returns {string[]} The steps that apply to the choices made so far. */
  #steps() {
    const state = this.#state;
    const steps = ['model', 'color'];
    // Before a color is chosen, assume a size step when the hat has a Size option.
    if (state.color ? this.#sizeChoices > 1 : !state.model || this.#hasSizeOption) steps.push('size');
    steps.push('deco');

    if (state.deco) {
      const kind = this.#kind();
      if (kind === 'patch') {
        if (this.#choices('pcolor').length) steps.push('pcolor');
        steps.push('cut');
        if (state.cut?.shapes) steps.push('shape');
        steps.push('place');
      } else if (kind === 'embroidery') {
        steps.push('place');
      }
    }
    steps.push('upload', 'qty');
    return steps;
  }

  /** @param {string} key */
  #clearAfter(key) {
    for (const later of ORDER.slice(ORDER.indexOf(key) + 1)) delete this.#state[later];
    if (ORDER.indexOf(key) < ORDER.indexOf('deco')) this.#variant = null;
    this.#rows = [];
    this.refs.rows.replaceChildren();
    this.#vectorize = false;
    this.#digitize = false;
    this.#fileType = '';
    for (const input of [this.refs.file, this.refs.stitchFile, this.refs.pdfFile]) input.value = '';
  }

  /** @returns {boolean} True when embroidery still asks for the stitch file and the PDF. */
  #needsEmbroideryFiles() {
    return this.#kind() === 'embroidery' && !this.#digitize;
  }

  /** @returns {string} The service variant to add with the hat, or '' for none. */
  #serviceId() {
    const wanted = this.#kind() === 'embroidery' ? this.#digitize : this.#vectorize;
    return wanted ? this.dataset.serviceVariantId ?? '' : '';
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {string} The chosen file's extension in lower case, or '' when no file is chosen.
   */
  #extension(input) {
    const file = input.files?.[0];
    return file ? (file.name.split('.').pop() ?? '').toLowerCase() || 'unknown' : '';
  }

  /**
   * Works out whether the upload step is answered: a .dst and a .pdf for embroidery, or
   * one accepted artwork file otherwise (including embroidery with digitizing added).
   */
  #syncUpload() {
    const { file, stitchFile, pdfFile } = this.refs;
    this.#fileType = this.#extension(file);

    if (this.#needsEmbroideryFiles()) {
      const complete = this.#extension(stitchFile) === 'dst' && this.#extension(pdfFile) === 'pdf';
      this.#state.upload = complete ? { value: `${stitchFile.files?.[0]?.name}, ${pdfFile.files?.[0]?.name}` } : undefined;
      return;
    }

    const accepted = VECTOR_TYPES.includes(this.#fileType) || RASTER_TYPES.includes(this.#fileType);
    this.#state.upload = accepted ? { value: file.files?.[0]?.name ?? '' } : undefined;
  }

  /** @param {HTMLElement} tile */
  async #choose(tile) {
    const key = tile.dataset.step;
    const value = tile.dataset.value;
    if (this.#busy || !key || !value) return;

    const changed = this.#state[key]?.value !== value;
    if (changed) {
      this.#clearAfter(key);
      this.#state[key] = {
        value,
        id: tile.dataset.optionValueId,
        url: tile.dataset.url,
        shapes: tile.hasAttribute('data-shapes'),
      };

      try {
        if (key === 'model') await this.#loadModel();
        if (key === 'color') await this.#loadColor();
        if (key === 'size') await this.#loadSize();
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        delete this.#state[key];
        this.#setBusy(false);
        this.#showStatus(error instanceof Error && error.message === 'unavailable' ? 'unavailable' : 'error');
        this.#render(false);
        return;
      }

      if (key === 'deco') {
        this.#variant = {
          id: tile.dataset.variantId ?? '',
          price: tile.dataset.price ?? '',
          cents: Number(tile.dataset.priceCents) || 0,
        };
      }
    }

    this.#showStatus('');
    const steps = this.#steps();
    this.#current = Math.min(steps.indexOf(key) + 1, steps.length - 1);
    this.#render(true);
  }

  /**
   * Ask Shopify for the product's option tiles, resolved for the given option values.
   * @param {string[]} ids
   * @returns {Promise<HTMLElement>}
   */
  async #fetchOptions(ids) {
    const model = this.#state.model;
    if (!model?.url) throw new Error('error');

    this.#request?.abort();
    this.#request = new AbortController();
    this.#setBusy(true);

    const url = new URL(model.url, window.location.origin);
    url.searchParams.set('section_id', this.dataset.section ?? '');
    const selected = ids.filter(Boolean);
    if (selected.length) url.searchParams.set('option_values', selected.join(','));

    const response = await fetch(url, { signal: this.#request.signal });
    if (!response.ok) throw new Error('error');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const root = html.querySelector('.hat-builder-product');
    if (!(root instanceof HTMLElement)) throw new Error('error');

    this.#setBusy(false);
    return root;
  }

  /**
   * Replace a step's tiles with the ones Shopify rendered.
   * @param {HTMLElement} root
   * @param {'color' | 'size' | 'deco'} key
   */
  #fill(root, key) {
    const source = root.querySelector(`[data-option="${key}"]`);
    const box = this.querySelector(`[data-tiles="${key}"]`);
    if (!box) return;
    box.replaceChildren(...Array.from(source?.children ?? []).map((node) => document.importNode(node, true)));
  }

  /**
   * @param {HTMLElement} root
   * @param {string} key
   * @returns {string} The id of the option value Shopify treats as selected.
   */
  #selectedId(root, key) {
    const tile = root.querySelector(`[data-option="${key}"] [data-selected="true"]`);
    return tile instanceof HTMLElement ? tile.dataset.optionValueId ?? '' : '';
  }

  async #loadModel() {
    const root = await this.#fetchOptions([]);
    if (!root.querySelector('[data-option="color"]') || !root.querySelector('[data-option="deco"]')) throw new Error('error');

    this.#fill(root, 'color');
    this.#fill(root, 'size');
    this.#fill(root, 'deco');
    this.#ids = {
      color: this.#selectedId(root, 'color'),
      size: this.#selectedId(root, 'size'),
      deco: this.#selectedId(root, 'deco'),
    };
    this.#hasSizeOption = this.#tiles('size').length > 0;
    this.#sizeChoices = 0;
  }

  /** Reads which sizes exist for the chosen color. A color made in one size gets that size. */
  async #loadColor() {
    this.#ids.color = this.#state.color?.id ?? '';
    this.#sizeChoices = 0;
    await this.#refreshOptions();

    if (this.#hasSizeOption) {
      const sizes = this.#choices('size');
      if (!sizes.length) throw new Error('unavailable');
      this.#sizeChoices = sizes.length;

      const only = sizes.length === 1 ? sizes[0] : undefined;
      if (only) {
        this.#state.size = { value: only.dataset.value ?? '', id: only.dataset.optionValueId, auto: true };
        if (only.dataset.optionValueId !== this.#ids.size) {
          this.#ids.size = only.dataset.optionValueId ?? '';
          await this.#refreshOptions();
        }
      }
      // With several sizes the customer chooses next, and that choice loads the decorations.
      if (!only) return;
    }

    if (!this.#choices('deco').length) throw new Error('unavailable');
  }

  async #loadSize() {
    this.#ids.size = this.#state.size?.id ?? '';
    await this.#refreshOptions();
    if (!this.#choices('deco').length) throw new Error('unavailable');
  }

  async #refreshOptions() {
    const { color, size, deco } = this.#ids;
    const root = await this.#fetchOptions([color, size, deco]);
    this.#fill(root, 'size');
    this.#fill(root, 'deco');
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.refs.stage.setAttribute('aria-busy', String(busy));
    if (busy) this.#showStatus('loading');
  }

  /**
   * Blocks a second send while the first is uploading. If the browser never leaves the
   * page (for example the connection dropped), sending is allowed again after a while.
   * @param {boolean} sending
   */
  #setSending(sending) {
    clearTimeout(this.#sendingTimer);
    this.#sending = sending;
    this.#showStatus(sending ? 'sending' : '');
    // The out-of-stock answer travels as a line item property, so the radios are not posted.
    for (const radio of this.#stockRadios()) radio.disabled = sending;
    if (sending) this.#sendingTimer = setTimeout(() => this.#setSending(false), 30000);
  }

  /** @returns {HTMLInputElement[]} */
  #stockRadios() {
    return Array.from(this.refs.stock?.querySelectorAll('input[data-stock-choice]') ?? []);
  }

  /** @returns {string} The chosen "If out of stock" answer, or '' when none is chosen yet. */
  #stockChoice() {
    return this.#stockRadios().find((radio) => radio.checked)?.value ?? '';
  }

  /** @param {'' | 'loading' | 'error' | 'unavailable' | 'sending'} kind */
  #showStatus(kind) {
    const { status } = this.refs;
    status.textContent = kind ? status.dataset[kind] ?? '' : '';
    status.hidden = !kind;
  }

  /** @returns {boolean} True only when every step is answered and a real, purchasable variant is chosen. */
  #ready() {
    return (
      this.#steps().every((key) => this.#state[key]) &&
      Boolean(this.#variant?.id) &&
      Boolean(this.#state.upload) &&
      this.#rows.length > 0 &&
      this.#rows.every((row) => row.variantId && row.quantity >= 1) &&
      // When the out-of-stock question is shown, it must be answered.
      (!this.refs.stock || this.#stockChoice() !== '') &&
      !this.#busy
    );
  }

  /** @param {boolean} moved - True when the step changed, to slide it in and move focus. */
  #render(moved) {
    const state = this.#state;
    const refs = this.refs;
    const steps = this.#steps();
    this.#current = Math.min(this.#current, steps.length - 1);
    const key = this.#previewPanel || steps[this.#current] || 'model';
    // The quantity step starts with one row: the color and size already chosen.
    if (key === 'qty' && !this.#rows.length) this.#startRows();

    for (const panel of this.querySelectorAll('[data-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      const active = panel.dataset.panel === key;
      panel.hidden = !active;
      panel.removeAttribute('data-enter');
      if (active && moved) {
        // Restart the slide-in animation.
        void panel.offsetWidth;
        panel.setAttribute('data-enter', '');
        const heading = panel.querySelector('.hat-builder__panel-heading');
        if (heading instanceof HTMLElement) heading.focus();
      }
    }

    for (const step of ORDER) {
      const allowed = new Set(this.#choices(step));
      for (const tile of this.#tiles(step)) {
        tile.hidden = !allowed.has(tile);
        tile.setAttribute('aria-pressed', String(state[step]?.value === tile.dataset.value));
      }
    }

    const kind = this.#kind();
    for (const help of this.querySelectorAll('[data-panel="place"] .hat-builder__help')) {
      if (help instanceof HTMLElement) help.hidden = help.dataset.for !== kind;
    }
    if (refs.dtfNote) refs.dtfNote.hidden = (state.deco?.value ?? '').toLowerCase() !== 'dtf';

    // "<color> comes in <size> only."
    const autoSize = state.size?.auto ? state.size.value : '';
    refs.sizeNote.hidden = !autoSize;
    refs.sizeNote.textContent = autoSize
      ? (this.dataset.oneSizeNote ?? '').replace('[color]', state.color?.value ?? '').replace('[size]', autoSize)
      : '';

    this.#renderChips(steps);

    refs.progress.replaceChildren(
      ...steps.map((step) => {
        const segment = document.createElement('span');
        if (state[step]) segment.setAttribute('data-done', '');
        return segment;
      })
    );

    const price = this.#variant?.price ?? '';
    refs.price.textContent = price || '—';
    // Embroidery calls the service "digitizing"; the other decorations call it "vectorization".
    const embroidery = kind === 'embroidery';
    const twoFiles = this.#needsEmbroideryFiles();
    if (refs.priceNote) {
      const data = this.dataset;
      const note = embroidery
        ? this.#digitize
          ? data.priceNoteDigitizing
          : data.priceNoteEmbroidery
        : this.#vectorize
        ? data.priceNoteVectorization
        : data.priceNote;
      refs.priceNote.textContent = note ?? '';
    }

    refs.embroideryFiles.hidden = !twoFiles;
    refs.artworkField.hidden = twoFiles;
    if (refs.uploadHelp) refs.uploadHelp.hidden = embroidery;
    if (refs.digitizeBox) refs.digitizeBox.hidden = !embroidery;
    const stitchType = this.#extension(refs.stitchFile);
    const pdfType = this.#extension(refs.pdfFile);
    refs.stitchMessage.hidden = !twoFiles || !stitchType || stitchType === 'dst';
    refs.pdfMessage.hidden = !twoFiles || !pdfType || pdfType === 'pdf';

    const artworkAccepted = VECTOR_TYPES.includes(this.#fileType) || RASTER_TYPES.includes(this.#fileType);
    refs.vectorMessage.hidden = embroidery || !VECTOR_TYPES.includes(this.#fileType);
    refs.rasterMessage.hidden = embroidery || !RASTER_TYPES.includes(this.#fileType);
    refs.typeMessage.hidden = twoFiles || !this.#fileType || artworkAccepted;
    /** @type {[HTMLButtonElement | undefined, boolean][]} */
    const toggles = [
      [refs.vectorize, this.#vectorize],
      [refs.digitize, this.#digitize],
    ];
    for (const [toggle, on] of toggles) {
      if (!toggle) continue;
      toggle.setAttribute('aria-pressed', String(on));
      for (const label of toggle.querySelectorAll('span')) label.hidden = label.hasAttribute('data-on') !== on;
    }
    if (refs.vectorize) refs.vectorize.hidden = embroidery || !RASTER_TYPES.includes(this.#fileType);
    // The ownership notice sits under its toggle and shows only with it.
    if (refs.vectorizeNotice) refs.vectorizeNotice.hidden = refs.vectorize?.hidden ?? true;
    for (const notice of this.querySelectorAll('[data-notice-for]')) {
      if (notice instanceof HTMLElement) notice.hidden = (notice.dataset.noticeFor === 'embroidery') !== embroidery;
    }

    this.#updateRows();
    refs.back.disabled = this.#current === 0;
    refs.next.disabled = !state[key] || this.#current >= steps.length - 1;
  }

  /**
   * The customer's choices so far. Tapping one goes back to that step.
   * @param {string[]} steps
   */
  #renderChips(steps) {
    const chips = [];
    for (const key of ORDER) {
      const choice = this.#state[key];
      if (!choice) continue;

      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'hat-builder__chip';
      // A size set automatically has no step of its own; its chip goes back to the color.
      chip.dataset.chip = steps.includes(key) ? key : 'color';

      const label = document.createElement('span');
      label.className = 'hat-builder__chip-label';
      label.textContent = this.#panel(key)?.dataset.label ?? '';
      const value = document.createElement('span');
      value.className = 'hat-builder__chip-value';
      value.textContent = choice.value;
      chip.append(label, value);
      chips.push(chip);
    }
    this.refs.chips.replaceChildren(...chips);
    if (this.refs.empty) this.refs.empty.hidden = chips.length > 0;
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const { back, next, vectorize, digitize } = this.refs;

    const tile = target.closest('.hat-builder__tile');
    if (tile instanceof HTMLElement && this.contains(tile)) {
      this.#choose(tile);
      return;
    }

    const chip = target.closest('.hat-builder__chip');
    if (chip instanceof HTMLElement && !this.#busy) {
      this.#current = Math.max(0, this.#steps().indexOf(chip.dataset.chip ?? ''));
      this.#render(true);
      return;
    }

    if (back.contains(target) || next.contains(target)) {
      if (this.#busy) return;
      const last = this.#steps().length - 1;
      this.#current = Math.min(last, Math.max(0, this.#current + (back.contains(target) ? -1 : 1)));
      this.#render(true);
      return;
    }

    if (this.refs.addRow.contains(target)) {
      this.#addRow();
      return;
    }

    const remove = target.closest('[data-row-remove]');
    if (remove instanceof HTMLElement && !this.#busy) {
      this.#rows.splice(this.#rowIndex(remove), 1);
      this.#buildRows();
      this.#renderChips(this.#steps());
      this.refs.addRow.focus();
      return;
    }

    if (vectorize?.contains(target)) {
      this.#vectorize = !this.#vectorize;
      this.#render(false);
      return;
    }

    // Embroidery: swaps the stitch file and PDF for the service plus one artwork file.
    if (digitize?.contains(target)) {
      this.#digitize = !this.#digitize;
      this.#syncUpload();
      this.#render(false);
    }
  };

  /** @param {Event} event */
  #onFileChange = (event) => {
    if (event.target instanceof HTMLElement && event.target.closest('.hat-builder__row')) {
      this.#onRowChange(event.target);
      return;
    }
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-stock-choice]')) {
      this.#updateRows();
      return;
    }
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'file') return;
    if (event.target === this.refs.file) this.#vectorize = false;
    this.#syncUpload();
    this.#render(false);
  };

  /**
   * @param {Element} element - Anything inside a quantity row.
   * @returns {number} The row's position in the list.
   */
  #rowIndex(element) {
    const row = element.closest('.hat-builder__row');
    return row instanceof HTMLElement ? Number(row.dataset.row) : -1;
  }

  /** @param {number} cents */
  #money(cents) {
    const format = this.refs.moneyFormat?.content.textContent || '{{amount}}';
    return formatMoney(cents, format, this.dataset.currency ?? '');
  }

  #startRows() {
    const { color, size } = this.#state;
    if (!this.#variant || !color) return;
    this.#rows = [
      {
        colorId: color.id ?? '',
        colorName: color.value,
        sizeId: size?.id ?? '',
        sizeName: size?.value ?? '',
        sizes: [],
        variantId: this.#variant.id,
        cents: this.#variant.cents,
        quantity: 1,
      },
    ];
    this.#buildRows();
  }

  /**
   * Ask Shopify which sizes exist for a color and which variant matches the color, the
   * size and the chosen decoration. A size the color is not made in is replaced by the
   * first one it is made in, so a row can never hold a combination without a variant.
   * @param {string} colorId
   * @param {string} sizeId
   * @returns {Promise<Omit<Row, 'quantity'>>}
   */
  async #resolveRow(colorId, sizeId) {
    const decoId = this.#state.deco?.id ?? '';
    const tilesOf = (/** @type {HTMLElement} */ source, /** @type {string} */ option) =>
      /** @type {HTMLElement[]} */ (Array.from(source.querySelectorAll(`[data-option="${option}"] .hat-builder__tile`)));
    const purchasable = (/** @type {HTMLElement} */ tile) => Boolean(tile.dataset.variantId) && tile.dataset.variantAvailable === 'true';

    let root = await this.#fetchOptions([colorId, sizeId, decoId]);
    /** @type {{ id: string, name: string }[]} */
    let sizes = [];
    if (this.#hasSizeOption) {
      sizes = tilesOf(root, 'size')
        .filter(purchasable)
        .map((tile) => ({ id: tile.dataset.optionValueId ?? '', name: tile.dataset.value ?? '' }));
      const first = sizes[0];
      if (!first) throw new Error('unavailable');
      if (!sizes.some((size) => size.id === sizeId)) {
        sizeId = first.id;
        root = await this.#fetchOptions([colorId, sizeId, decoId]);
      }
    }

    const decoration = tilesOf(root, 'deco').find((tile) => tile.dataset.optionValueId === decoId);
    if (!decoration || !purchasable(decoration)) throw new Error('unavailable');

    return {
      colorId,
      colorName: this.#tiles('color').find((tile) => tile.dataset.optionValueId === colorId)?.dataset.value ?? '',
      sizeId,
      sizeName: sizes.find((size) => size.id === sizeId)?.name ?? '',
      sizes,
      variantId: decoration.dataset.variantId ?? '',
      cents: Number(decoration.dataset.priceCents) || 0,
    };
  }

  /**
   * Loads a row's variant for a color and size, then redraws the rows.
   * @param {number} index - The row to change, or the list's length to add a row.
   * @param {string} colorId
   * @param {string} sizeId
   * @param {string} focus - The control to focus in that row afterwards.
   */
  async #loadRow(index, colorId, sizeId, focus) {
    if (this.#busy) return;
    try {
      const resolved = await this.#resolveRow(colorId, sizeId);
      this.#rows[index] = { ...resolved, quantity: this.#rows[index]?.quantity ?? 1 };
      this.#showStatus('');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#setBusy(false);
      this.#showStatus(error instanceof Error && error.message === 'unavailable' ? 'unavailable' : 'error');
    }
    this.#buildRows();
    this.#renderChips(this.#steps());
    const control = this.refs.rows.querySelector(`[data-row="${index}"] ${focus}`);
    if (control instanceof HTMLElement) control.focus();
  }

  /** A new row starts as the last row's color and size. */
  #addRow() {
    const last = this.#rows[this.#rows.length - 1];
    if (last) this.#loadRow(this.#rows.length, last.colorId, last.sizeId, '[data-row-color]');
  }

  /** Draws every row from the template. Called when rows are added, removed or reloaded. */
  #buildRows() {
    const { rows, rowTemplate, file } = this.refs;
    const colors = this.#tiles('color');
    const option = (/** @type {string} */ value, /** @type {string} */ text, /** @type {boolean} */ selected) => {
      const element = document.createElement('option');
      element.value = value;
      element.textContent = text;
      element.selected = selected;
      return element;
    };

    rows.replaceChildren(
      ...this.#rows.map((row, index) => {
        const element = /** @type {HTMLElement} */ (rowTemplate.content.firstElementChild?.cloneNode(true));
        const first = index === 0;
        element.dataset.row = String(index);

        /** @type {Record<string, HTMLElement | null>} */
        const controls = {
          color: element.querySelector('[data-row-color]'),
          size: element.querySelector('[data-row-size]'),
          quantity: element.querySelector('[data-row-quantity]'),
        };
        const colorText = element.querySelector('[data-row-color-text]');
        const sizeText = element.querySelector('[data-row-size-text]');
        const sizeField = element.querySelector('[data-row-size-field]');
        const remove = element.querySelector('[data-row-remove]');

        // The first row is the color and size chosen in the steps, shown as text.
        if (colorText instanceof HTMLElement) {
          colorText.textContent = row.colorName;
          colorText.hidden = !first;
        }
        if (sizeText instanceof HTMLElement) {
          sizeText.textContent = row.sizeName;
          sizeText.hidden = !first;
        }
        if (controls.color) {
          controls.color.hidden = first;
          if (!first) {
            controls.color.append(
              ...colors.map((tile) => option(tile.dataset.optionValueId ?? '', tile.dataset.value ?? '', tile.dataset.optionValueId === row.colorId))
            );
          }
        }
        if (controls.size) {
          controls.size.hidden = first;
          controls.size.append(...row.sizes.map((size) => option(size.id, size.name, size.id === row.sizeId)));
        }
        if (sizeField instanceof HTMLElement) sizeField.hidden = !this.#hasSizeOption;
        if (controls.quantity instanceof HTMLInputElement) controls.quantity.value = String(row.quantity);
        if (remove instanceof HTMLElement) remove.hidden = first;

        for (const label of element.querySelectorAll('[data-row-label]')) {
          const control = label instanceof HTMLLabelElement ? controls[label.dataset.rowLabel ?? ''] : null;
          if (!control || !(label instanceof HTMLLabelElement)) continue;
          control.id = `${file.id}-row-${index}-${label.dataset.rowLabel}`;
          if (!control.hidden) label.htmlFor = control.id;
        }
        return element;
      })
    );
    this.#updateRows();
  }

  /** Refreshes the row prices, the total, the quantity answer and the Add to cart button. */
  #updateRows() {
    const each = this.dataset.eachLabel ?? '[price]';
    let total = 0;
    let count = 0;
    this.#rows.forEach((row, index) => {
      const line = row.cents * row.quantity;
      total += line;
      count += row.quantity;
      const price = this.refs.rows.querySelector(`[data-row="${index}"] [data-row-price]`);
      if (price) price.textContent = each.replace('[price]', this.#money(row.cents)) + (row.quantity > 1 ? ` = ${this.#money(line)}` : '');
    });

    const { total: totalElement, submit } = this.refs;
    totalElement.textContent = this.#rows.length ? this.#money(total) : '—';
    if (this.#rows.length) this.#state.qty = { value: String(count) };
    submit.textContent = (this.dataset.addLabel ?? '').replace('[price]', this.#rows.length ? this.#money(total) : '');
    submit.disabled = !this.#ready();
  }

  /**
   * Typing a quantity updates the totals straight away.
   * @param {Event} event
   */
  #onRowInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-row-quantity]')) return;
    const row = this.#rows[this.#rowIndex(input)];
    if (!row) return;
    row.quantity = Math.max(1, Math.floor(Number(input.value)) || 1);
    this.#updateRows();
    this.#renderChips(this.#steps());
  };

  /** @param {HTMLElement} target - The row control that changed. */
  #onRowChange(target) {
    const index = this.#rowIndex(target);
    const row = this.#rows[index];
    if (!row) return;

    if (target instanceof HTMLInputElement) {
      // Leaving the field: show the quantity that will be used (never below 1).
      target.value = String(row.quantity);
    } else if (target instanceof HTMLSelectElement && target.matches('[data-row-color]')) {
      this.#loadRow(index, target.value, row.sizeId, '[data-row-color]');
    } else if (target instanceof HTMLSelectElement && target.matches('[data-row-size]')) {
      this.#loadRow(index, row.colorId, target.value, '[data-row-size]');
    }
  }

  /**
   * Writes the cart fields, one line per quantity row, then lets the browser post the form
   * (multipart, so the files are uploaded as line item properties). Shopify answers by
   * showing the cart.
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    if (!this.#ready() || !this.#variant || this.#sending) {
      event.preventDefault();
      return;
    }

    const serviceId = this.#serviceId();
    const rows = this.#rows;
    // One hat line and no service uses Shopify's plain product fields. Anything more goes as "items".
    const list = rows.length > 1 || Boolean(serviceId);
    const field = (/** @type {number} */ index, /** @type {string} */ name) => (list ? `items[${index}][${name}]` : name);
    const property = (/** @type {number} */ index, /** @type {string} */ name) =>
      list ? `items[${index}][properties][${name}]` : `properties[${name}]`;
    const artwork = this.#panel('upload')?.dataset.property ?? 'Artwork';

    /** @type {[string, string][]} */
    const values = [];
    const steps = this.#steps();
    const stockName = this.refs.stock?.dataset.property;
    const stockChoice = this.#stockChoice();
    rows.forEach((row, index) => {
      values.push([field(index, 'id'), row.variantId], [field(index, 'quantity'), String(row.quantity)]);
      if (stockName && stockChoice) values.push([property(index, stockName), stockChoice]);
      for (const key of PROPERTY_STEPS) {
        const choice = this.#state[key];
        const name = this.#panel(key)?.dataset.property;
        if (choice && name && steps.includes(key)) values.push([property(index, name), choice.value]);
      }
      // A file field uploads once, so the files ride on the first line only.
      if (index > 0) values.push([property(index, artwork), this.dataset.sameAsFirst ?? '']);
    });
    if (serviceId) {
      // The service is added once, however many rows there are.
      const index = rows.length;
      values.push(
        [`items[${index}][id]`, serviceId],
        [`items[${index}][quantity]`, '1'],
        [`items[${index}][properties][For]`, `${this.#state.model?.value ?? ''} - ${this.#state.color?.value ?? ''}`]
      );
    }

    // Only the files this path asks for get a name, so only they are uploaded.
    const { file, stitchFile, pdfFile } = this.refs;
    const twoFiles = this.#needsEmbroideryFiles();
    file.name = twoFiles ? '' : property(0, artwork);
    for (const input of [stitchFile, pdfFile]) {
      input.name = twoFiles && input.dataset.property ? property(0, input.dataset.property) : '';
    }
    this.refs.fields.replaceChildren(
      ...values.map(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        return input;
      })
    );
    this.#setSending(true);
  };

  /**
   * Theme editor: show the step that holds the block being edited.
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const tile = event.target instanceof Element ? event.target.closest('.hat-builder__tile') : null;
    if (!(tile instanceof HTMLElement)) return;
    this.#previewPanel = tile.dataset.step ?? '';
    this.#previewDecoration = tile.dataset.decoration ?? '';
    this.#render(false);
  };

  #onBlockDeselect = () => {
    this.#previewPanel = '';
    this.#previewDecoration = '';
    this.#render(false);
  };
}

if (!customElements.get('hat-builder')) {
  customElements.define('hat-builder', HatBuilder);
}
